/**
 * Where the loose parts of an assembly lie before a student picks them up
 * (ADR-025).
 *
 * Two jobs, both arithmetic, so both are here where they can be tested rather
 * than in the renderer where they cannot:
 *
 * - **Not in the lesson's order.** `parts` is written the way a person reads a
 *   body, head to toe. Laid out in that order the pile would hand over the
 *   answer the step exists to ask for.
 * - **Not on top of each other.** Two bones overlapping is a pinch that picks
 *   up the wrong one, and a ten-year-old will not know why.
 */

/** A part's footprint on the table, in the assembly's own units (metres). */
export type Footprint = { id: string; width: number; depth: number };

/**
 * A small seeded generator. Seeded by the lesson rather than the clock so the
 * pile is the same on every headset in the room: a teacher can say "the one on
 * the far left" and thirty children look at the same bone.
 */
function seededRandom(seed: string): () => number {
  let state = 2166136261;
  for (const char of seed) state = Math.imul(state ^ char.charCodeAt(0), 16777619);

  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The parts in an order that gives nothing away, the same every time. */
export function shuffled<T>(items: readonly T[], seed: string): T[] {
  const random = seededRandom(seed);
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * Rows on a table, left to right, wrapping when the table runs out.
 *
 * Each row is centred on x = 0 -- in front of the skeleton -- and rows grow
 * towards +z, which is towards the student once the lesson has turned to face
 * them. So the pile starts at the skeleton's feet and ends within reach.
 */
export function layOut(
  parts: readonly Footprint[],
  options: { maxWidth: number; gap: number; startZ: number },
): Map<string, { x: number; z: number }> {
  const rows: Footprint[][] = [];
  let row: Footprint[] = [];
  let width = 0;

  for (const part of parts) {
    const added = row.length === 0 ? part.width : width + options.gap + part.width;
    if (row.length > 0 && added > options.maxWidth) {
      rows.push(row);
      row = [part];
      width = part.width;
    } else {
      row.push(part);
      width = added;
    }
  }
  if (row.length > 0) rows.push(row);

  const at = new Map<string, { x: number; z: number }>();
  let near = options.startZ;

  for (const current of rows) {
    const rowWidth =
      current.reduce((sum, part) => sum + part.width, 0) + options.gap * (current.length - 1);
    const depth = Math.max(...current.map((part) => part.depth));

    let left = -rowWidth / 2;
    for (const part of current) {
      at.set(part.id, { x: left + part.width / 2, z: near + part.depth / 2 });
      left += part.width + options.gap;
    }
    near += depth + options.gap;
  }
  return at;
}
